import * as React from 'react';

import { CATEGORY_MAP } from '@src/config/maps';
import { useSurvey } from '@src/hooks/useSurvey';
import {
  SURVEY_CATEGORIES_EVENING,
  SURVEY_CATEGORIES_MORNING,
  SURVEY_CATEGORY_FILTERS,
  SurveyedCategoriesType
} from '@src/services/surveys/settings';
import { getAlgoliaProducts } from '@src/utils/algolia-products';
import { SurveyQuestionProps } from './SurveyQuestion';
import { TypeAlgolia } from './TypeAlgolia';

export interface TypeProductsProps extends SurveyQuestionProps {}

/**
 * @name TypeProducts
 * @description This component is used in the original "Full Survey" where
 * we collect the users morning and evening products.
 */
export const TypeProducts = (props: TypeProductsProps) => {
  const { question } = props;

  // Hooks
  const { atom, setAnswer } = useSurvey();

  // Handlers
  const onClickSameAs = () => {
    SURVEY_CATEGORIES_MORNING.map((category) => {
      const destCategory = `${question.name}.evening.${category}`;
      const destProductId = `${question.name}.evening.${category}_id`;
      const srcCategory = `${question.name}.morning.${category}`;
      const srcProductId = `${question.name}.morning.${category}_id`;

      const srcId = atom.answers[srcProductId];
      const srcValue = atom.answers[srcCategory];

      setAnswer(destCategory, srcValue);
      setAnswer(destProductId, srcId);
    });
  };

  // Markup
  const renderAlgoliaInput = (
    category: SurveyedCategoriesType,
    evening = false
  ) => {
    const categoryFilters = SURVEY_CATEGORY_FILTERS[category];
    const time = evening ? 'evening' : 'morning';
    const keyCategory = `${question.name}.${time}.${category}`;
    const keyProductId = `${question.name}.${time}.${category}_id`;
    const value = atom.answers[keyCategory];
    const placeholder = category === 'other' ? 'Other' : CATEGORY_MAP[category];

    const suggestionPlugin = getAlgoliaProducts({
      category,
      categoryFilters,
      onSelect: (event) => {
        setAnswer(keyCategory, event.inputValue);
        setAnswer(keyProductId, event.productId);
      }
    });

    return (
      <TypeAlgolia
        className="type-products rounded-none font-beatrice-light text-14 md:text-18"
        key={category}
        onChange={(text) => {
          setAnswer(keyCategory, text);
          setAnswer(keyProductId, '');
        }}
        placeholder={placeholder}
        suggestionPlugin={suggestionPlugin}
        value={value}
      />
    );
  };

  return (
    <div className="relative bottom-10 flex flex-col gap-0">
      <div className="mb-1 text-left align-top text-xl font-bold md:mt-2x md:text-2xl">
        MORNING
      </div>
      <div className="flex flex-col gap-2">
        {SURVEY_CATEGORIES_MORNING.map((node) =>
          renderAlgoliaInput(node, false)
        )}
      </div>

      {/* Sync evening products with any morning values */}
      <div className="mt-8 flex flex-row items-center gap-x-8">
        <div className="mb-1 text-left text-xl font-bold md:text-2xl">
          NIGHT
        </div>
        <div className="flex flex-row items-center">
          <input
            id="default-checkbox"
            type="checkbox"
            onClick={onClickSameAs}
            className="mb-2 flex h-4 w-4 rounded border-gray-3 focus:ring-black"
          ></input>
          <label htmlFor="default-checkbox" className="mt-0 mb-1 ml-1 flex">
            Same as morning
          </label>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {SURVEY_CATEGORIES_EVENING.map((node) =>
          renderAlgoliaInput(node, true)
        )}
      </div>
    </div>
  );
};
